import React, { useState } from 'react'

import clsx from 'clsx'

import { IconButton } from 'components/Button'
import Html from 'components/Html'
import Icon from 'components/Icon'
import { useEventContext } from 'context/EventContext'
import { RESOURCES_DEFAULT_AVATAR_WIDTH } from 'screens/resources/constants'

import classes from './AuthorCard.module.scss'

/** @type {import("./types").default} */
const AuthorCard = ({ author, className, hasBioToggle, isFullWidth, tipText, hasLink, classes: propClasses }) => {
  const [showBio, setShowBio] = useState(false)
  const eventContent = useEventContext({
    name: 'click',
    engagementComponent: 'author-card',
  })

  const showBioToggler = hasBioToggle && author.bio

  return (
    <div
      className={clsx('AuthorCard', classes.root, className, {
        [classes.hasBioToggle]: showBioToggler,
        [classes.hasAvatar]: author.avatar,
        [classes.fullWidth]: isFullWidth,
      })}
    >
      {author.avatar && (
        <img
          alt="avatar"
          data-testid="AuthorCard-avatar"
          className={classes.avatar}
          src={`${author.avatar.url}${RESOURCES_DEFAULT_AVATAR_WIDTH}`}
        />
      )}
      <div
        className={clsx(classes.content, propClasses?.content, {
          [classes.hasAvatar]: author.avatar,
          [classes.wide]: isFullWidth,
        })}
      >
        {tipText && (
          <span className={clsx(classes.authorTip, propClasses?.tip)}>
            {tipText} <Icon icon={['ga', 'author-chevron']} className={classes.authorTipIcon} size={20} />
          </span>
        )}
        <div className={classes.info}>
          <p className={classes.name}>{author.authorName}</p>
          <div className={classes.title}>{author.authorTitle}</div>
          <div className={classes.social} data-testid="author-card-social-icons">
            {showBioToggler && (
              <div
                role="button"
                tabIndex="0"
                {...eventContent.addEvent({
                  engagementDestination: showBio ? 'collapse' : 'expand',
                  engagementDetails: 'bio-toggle',
                })}
                onClick={() => setShowBio(!showBio)}
                className={classes.bioToggler}
              >
                <span>{showBio ? 'Hide Bio' : 'Read Bio'}</span>
                <Icon icon={showBio ? 'chevron-up' : 'chevron-down'} prefix="far" />
              </div>
            )}
            {author.twitterUrl && (
              <IconButton
                className={propClasses?.socialButton}
                {...eventContent.addEvent({
                  name: 'click',
                  engagementDestination: 'go-to_social_twitter',
                  engagementDetails: 'icon_social_twitter',
                  outboundSocialLinkClickCount: 1,
                  outboundLinkType: 'social link',
                  clickUrl: author.twitterUrl,
                })}
                href={author.twitterUrl}
                target="_blank"
                rel="noopener"
              >
                <Icon icon="x-twitter" prefix="fab" size="24" />
              </IconButton>
            )}
            {author.linkedInUrl && (
              <IconButton
                className={propClasses?.socialButton}
                {...eventContent.addEvent({
                  engagementDestination: 'go-to_social_linkedin',
                  engagementDetails: 'icon_social_linkedin',
                  outboundSocialLinkClickCount: 1,
                  outboundLinkType: 'social link',
                  clickUrl: author.linkedInUrl,
                })}
                href={author.linkedInUrl}
                target="_blank"
                rel="noopener"
              >
                <Icon icon="linkedin" prefix="fab" size="24" />
              </IconButton>
            )}
          </div>
        </div>
        <div>
          {author.bio && (
            <Html
              className={clsx(classes.bio, {
                [classes.showBio]: showBio,
              })}
              Component="div"
            >
              {author.bio.replace(/\n/g, '<br />')}
            </Html>
          )}
          {hasLink && (
            <a
              className={clsx(classes.authorButton, propClasses?.button)}
              href={`/resources/author/${author.slug}/`}
              {...eventContent.addEvent({
                name: 'engagement_facet_click',
                engagementDestination: 'go-to_author',
                engagementDetails: 'button_visit-author-page',
              })}
            >
              Visit author&apos;s page
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

export default AuthorCard
